package co.maytes.standupbot.dashboard.features.membersSettings

import co.maytes.standupbot.dashboard.features.integrations.LoadState
import co.maytes.standupbot.dashboard.features.settings.MemberAction
import co.maytes.standupbot.dashboard.features.settings.SettingsStore
import co.maytes.standupbot.dashboard.lib.api.ApiError
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.shared.dto.auth.RoleDto
import co.maytes.standupbot.shared.dto.workspace.CreateInviteLinkRequestDto
import co.maytes.standupbot.shared.dto.workspace.InviteDeliveryDto
import co.maytes.standupbot.shared.dto.workspace.MemberStatusDto
import co.maytes.standupbot.shared.dto.workspace.UpdateMemberRequestDto
import co.maytes.standupbot.shared.dto.workspace.WorkspaceMemberDto
import com.kttipay.mvi.api.Container
import com.kttipay.mvi.api.StoreScope
import com.kttipay.mvi.dsl.store
import kotlinx.coroutines.CoroutineScope

private typealias Scope = StoreScope<MembersSettingsContract.State, MembersSettingsContract.Action>

class MembersSettingsContainer(
    scope: CoroutineScope,
    private val settings: SettingsStore,
) : Container<MembersSettingsContract.State, MembersSettingsContract.Intent, MembersSettingsContract.Action> {

    override val store = scope.store<
            MembersSettingsContract.State,
            MembersSettingsContract.Intent,
            MembersSettingsContract.Action,
            >(initialState = MembersSettingsContract.State()) {
        configure(name = "MembersSettings")
        logging()
        reduce { intent ->
            when (intent) {
                is MembersSettingsContract.Intent.Load -> {
                    updateState {
                        copy(
                            workspaceId = intent.workspaceId,
                            canManage = intent.canManage,
                        )
                    }
                    load()
                }
                is MembersSettingsContract.Intent.SetSearch ->
                    updateState { copy(search = intent.value) }
                MembersSettingsContract.Intent.OpenInvite -> openInvite(MembersSettingsContract.InviteTab.SLACK)
                MembersSettingsContract.Intent.OpenInviteLinks -> openInvite(MembersSettingsContract.InviteTab.LINK)
                MembersSettingsContract.Intent.LoadInviteLinks -> loadInviteLinks()
                MembersSettingsContract.Intent.CloseInvite -> {
                    if (!currentState.inviteSubmitting) {
                        updateState { copy(inviteOpen = false, inviteError = null, inviteOutcomes = emptyList()) }
                    }
                }
                is MembersSettingsContract.Intent.SetInviteTab ->
                    updateState { copy(inviteTab = intent.tab, inviteError = null, inviteOutcomes = emptyList()) }
                MembersSettingsContract.Intent.ReloadDirectory -> loadDirectory()
                MembersSettingsContract.Intent.ClearInviteOutcomes ->
                    updateState { copy(inviteOutcomes = emptyList(), inviteError = null) }
                is MembersSettingsContract.Intent.SendSlackInvites ->
                    sendSlackInvites(intent.slackUserIds, intent.role)
                is MembersSettingsContract.Intent.SendEmailInvite ->
                    sendEmailInvite(intent.email, intent.role)
                is MembersSettingsContract.Intent.CreateInviteLink ->
                    createInviteLink(intent)
                is MembersSettingsContract.Intent.RevokeInviteLink ->
                    revokeInviteLink(intent.linkId)
                is MembersSettingsContract.Intent.OnMemberAction ->
                    onMemberAction(intent.member, intent.action)
                MembersSettingsContract.Intent.CancelDisable -> updateState { copy(pendingDisable = null) }
                MembersSettingsContract.Intent.ConfirmDisable -> confirmDisable()
                MembersSettingsContract.Intent.CancelRestore -> updateState { copy(pendingRestore = null) }
                MembersSettingsContract.Intent.ConfirmRestore -> confirmRestore()
                MembersSettingsContract.Intent.CancelErase -> updateState { copy(pendingErase = null) }
                MembersSettingsContract.Intent.ConfirmErase -> confirmErase()
                MembersSettingsContract.Intent.CancelTransfer ->
                    if (!currentState.transferSubmitting) {
                        updateState { copy(transferTo = null, transferError = null, transferNeedsSignIn = false) }
                    }
                is MembersSettingsContract.Intent.ConfirmTransfer -> confirmTransfer(intent.confirmWorkspaceName)
                is MembersSettingsContract.Intent.SetShowDisabled ->
                    updateState { copy(showDisabled = intent.value) }
                MembersSettingsContract.Intent.CancelInviteRevocation ->
                    updateState { copy(pendingInviteRevocation = null) }
                MembersSettingsContract.Intent.ConfirmInviteRevocation -> confirmInviteRevocation()
                MembersSettingsContract.Intent.CloseLinkLinear ->
                    updateState { copy(linkLinearFor = null) }
                is MembersSettingsContract.Intent.SubmitLinkLinear ->
                    submitLinkLinear(intent.linearUserId)
                MembersSettingsContract.Intent.CloseLinkGithub ->
                    updateState { copy(linkGithubFor = null) }
                is MembersSettingsContract.Intent.SubmitLinkGithub ->
                    submitLinkGithub(intent.githubLogin)
            }
        }
    }

    private suspend fun Scope.load() {
        val ws = currentState.workspaceId
        if (ws.isBlank()) return
        updateState { copy(status = MembersSettingsContract.Status.Loading) }
        settings.refreshMembers(ws)
        when (val ms = settings.membersState) {
            is LoadState.Error -> updateState { copy(status = MembersSettingsContract.Status.Error(ms.error)) }
            else -> updateState {
                copy(
                    members = settings.members.toList(),
                    status = MembersSettingsContract.Status.Ready,
                )
            }
        }
        if (currentState.members.any { it.status == MemberStatusDto.INVITED && it.slackUserId != null }) {
            loadDirectory()
        }
        if (currentState.canManage) loadInviteLinks()
    }

    private suspend fun Scope.reloadMembers() {
        val ws = currentState.workspaceId.ifBlank { return }
        settings.refreshMembers(ws)
        if (settings.membersState !is LoadState.Error) {
            updateState { copy(members = settings.members.toList()) }
        }
    }

    private suspend fun Scope.loadDirectory() {
        val ws = currentState.workspaceId.ifBlank { return }
        if (currentState.directoryLoading) return
        updateState { copy(directoryLoading = true, directoryFailed = false) }
        when (val r = settings.listSlackUsers(ws)) {
            is Result.Ok -> updateState { copy(directory = r.value, directoryLoading = false) }
            is Result.Err -> updateState { copy(directoryLoading = false, directoryFailed = true) }
        }
    }

    private suspend fun Scope.loadInviteLinks() {
        val ws = currentState.workspaceId.ifBlank { return }
        updateState { copy(inviteLinksLoading = true, inviteLinksFailed = false) }
        when (val r = settings.listInviteLinks(ws)) {
            is Result.Ok ->
                updateState { copy(inviteLinks = r.value, inviteLinksLoading = false, inviteLinksLoaded = true) }
            is Result.Err ->
                updateState {
                    copy(
                        inviteLinksLoading = false,
                        inviteLinksFailed = true,
                        inviteError = r.error.displayMessage.takeIf { inviteOpen } ?: inviteError,
                    )
                }
        }
    }

    private suspend fun Scope.openInvite(tab: MembersSettingsContract.InviteTab) {
        updateState {
            copy(
                inviteOpen = true,
                inviteTab = tab,
                inviteError = null,
                inviteOutcomes = emptyList(),
            )
        }
        if (currentState.directory.isEmpty()) loadDirectory()
        loadInviteLinks()
    }

    private suspend fun Scope.sendSlackInvites(
        slackUserIds: List<String>,
        role: RoleDto,
    ) {
        val ws = currentState.workspaceId.ifBlank { return }
        if (slackUserIds.isEmpty()) return
        updateState { copy(inviteSubmitting = true, inviteError = null, inviteOutcomes = emptyList()) }
        val directory = currentState.directory.associateBy { it.slackUserId }
        val outcomes = mutableListOf<MembersSettingsContract.InviteOutcome>()
        slackUserIds.forEach { id ->
            val label = directory[id]?.displayName ?: id
            when (val r = settings.inviteSlackUser(ws, id, role)) {
                is Result.Ok -> {
                    outcomes += MembersSettingsContract.InviteOutcome(label, r.value.delivery, r.value.acceptUrl)
                    updateState { copy(freshInviteUrls = freshInviteUrls + (r.value.member.userId to r.value.acceptUrl)) }
                }
                is Result.Err -> outcomes += MembersSettingsContract.InviteOutcome(label, error = r.error.displayMessage)
            }
        }
        reloadMembers()
        updateState { copy(inviteSubmitting = false, inviteOutcomes = outcomes) }
    }

    private suspend fun Scope.sendEmailInvite(
        email: String,
        role: RoleDto,
    ) {
        val ws = currentState.workspaceId.ifBlank { return }
        updateState { copy(inviteSubmitting = true, inviteError = null, inviteOutcomes = emptyList()) }
        when (val r = settings.inviteByEmail(ws, email, role)) {
            is Result.Ok -> {
                updateState {
                    copy(
                        freshInviteUrls = freshInviteUrls + (r.value.member.userId to r.value.acceptUrl),
                        inviteOutcomes =
                            listOf(MembersSettingsContract.InviteOutcome(email.trim(), r.value.delivery, r.value.acceptUrl)),
                    )
                }
                reloadMembers()
                updateState { copy(inviteSubmitting = false) }
            }
            is Result.Err -> updateState { copy(inviteSubmitting = false, inviteError = r.error.displayMessage) }
        }
    }

    private suspend fun Scope.createInviteLink(intent: MembersSettingsContract.Intent.CreateInviteLink) {
        val ws = currentState.workspaceId.ifBlank { return }
        updateState { copy(inviteSubmitting = true, inviteError = null) }
        val body = CreateInviteLinkRequestDto(intent.role, intent.expiresInDays, intent.maxUses, intent.label?.takeIf { it.isNotBlank() })
        when (val r = settings.createInviteLink(ws, body)) {
            is Result.Ok -> {
                updateState { copy(inviteSubmitting = false) }
                loadInviteLinks()
                action(MembersSettingsContract.Action.Notify("Invite link created", true))
            }
            is Result.Err -> updateState { copy(inviteSubmitting = false, inviteError = r.error.displayMessage) }
        }
    }

    private suspend fun Scope.revokeInviteLink(linkId: String) {
        val ws = currentState.workspaceId.ifBlank { return }
        when (val r = settings.revokeInviteLink(ws, linkId)) {
            is Result.Ok -> {
                loadInviteLinks()
                action(MembersSettingsContract.Action.Notify("Invite link revoked", true))
            }
            is Result.Err ->
                if (currentState.inviteOpen) {
                    updateState { copy(inviteError = r.error.displayMessage) }
                } else {
                    action(MembersSettingsContract.Action.Notify(r.error.displayMessage, false))
                }
        }
    }

    private suspend fun Scope.onMemberAction(
        member: WorkspaceMemberDto,
        action: MemberAction,
    ) {
        val ws = currentState.workspaceId.ifBlank { return }
        updateState { copy(actionError = null) }
        val invited = member.status == MemberStatusDto.INVITED
        when (action) {
            MemberAction.ChangeRoleToAdmin -> changeRole(ws, member, RoleDto.ADMIN, invited)
            MemberAction.ChangeRoleToMember -> changeRole(ws, member, RoleDto.MEMBER, invited)
            MemberAction.ChangeRoleToViewer -> changeRole(ws, member, RoleDto.VIEWER, invited)
            MemberAction.ResendInvite -> resend(ws, member)
            MemberAction.CopyInviteLink -> Unit
            MemberAction.RevokeInvite ->
                updateState { copy(pendingInviteRevocation = member) }
            MemberAction.Disable -> updateState { copy(pendingDisable = member) }
            MemberAction.Restore -> updateState { copy(pendingRestore = member) }
            MemberAction.Erase -> updateState { copy(pendingErase = member) }
            MemberAction.TransferOwnership ->
                updateState { copy(transferTo = member, transferError = null, transferNeedsSignIn = false) }
            MemberAction.LinkLinear ->
                updateState { copy(linkLinearFor = member, actionError = null) }
            MemberAction.LinkGithub ->
                updateState { copy(linkGithubFor = member, actionError = null) }
        }
    }

    private suspend fun Scope.resend(
        workspaceId: String,
        member: WorkspaceMemberDto,
    ) {
        when (val r = settings.resendInvite(workspaceId, member.userId)) {
            is Result.Ok -> {
                updateState { copy(freshInviteUrls = freshInviteUrls + (r.value.member.userId to r.value.acceptUrl)) }
                reloadMembers()
                action(MembersSettingsContract.Action.Notify(resendMessage(r.value.delivery), r.value.delivery != InviteDeliveryDto.DM_FAILED))
            }
            is Result.Err -> updateState { copy(actionError = r.error.displayMessage) }
        }
    }

    private fun resendMessage(delivery: InviteDeliveryDto): String =
        when (delivery) {
            InviteDeliveryDto.DM_SENT -> "Invite resent by Slack DM. The previous link no longer works."
            InviteDeliveryDto.DM_FAILED -> "Slack could not deliver the DM. Use Copy link and send it yourself."
            InviteDeliveryDto.LINK_ONLY -> "New link ready. Use Copy link and send it yourself."
        }

    private suspend fun Scope.changeRole(
        workspaceId: String,
        member: WorkspaceMemberDto,
        role: RoleDto,
        invited: Boolean,
    ) {
        if (invited) {
            when (val r = settings.updateInviteRole(workspaceId, member.userId, role)) {
                is Result.Ok -> reloadMembers()
                is Result.Err -> updateState { copy(actionError = r.error.displayMessage) }
            }
        } else {
            applyRole(workspaceId, member.userId, role)
        }
    }

    private suspend fun Scope.applyRole(
        workspaceId: String,
        userId: String,
        role: RoleDto,
    ) {
        when (val r = settings.updateMember(workspaceId, userId, UpdateMemberRequestDto(role = role))) {
            is Result.Ok -> updateState { copy(members = settings.members.toList()) }
            is Result.Err -> updateState { copy(actionError = r.error.displayMessage) }
        }
    }

    private suspend fun Scope.confirmInviteRevocation() {
        val state = currentState
        val invite = state.pendingInviteRevocation ?: return
        val ws = state.workspaceId.ifBlank { return }
        when (val r = settings.revokeInvite(ws, invite.userId)) {
            is Result.Ok -> {
                updateState { copy(pendingInviteRevocation = null, freshInviteUrls = freshInviteUrls - invite.userId) }
                reloadMembers()
                action(MembersSettingsContract.Action.Notify("Invite revoked", true))
            }
            is Result.Err -> updateState { copy(pendingInviteRevocation = null, actionError = r.error.displayMessage) }
        }
    }

    private suspend fun Scope.confirmDisable() {
        val state = currentState
        val target = state.pendingDisable ?: return
        val ws = state.workspaceId.ifBlank { return }
        updateState { copy(pendingDisable = null) }
        finishLifecycleChange(settings.disableMember(ws, target.userId), "${target.displayName} disabled")
    }

    private suspend fun Scope.confirmRestore() {
        val state = currentState
        val target = state.pendingRestore ?: return
        val ws = state.workspaceId.ifBlank { return }
        updateState { copy(pendingRestore = null) }
        finishLifecycleChange(settings.restoreMember(ws, target.userId), "${target.displayName} restored")
    }

    private suspend fun Scope.confirmErase() {
        val state = currentState
        val target = state.pendingErase ?: return
        val ws = state.workspaceId.ifBlank { return }
        updateState { copy(pendingErase = null) }
        finishLifecycleChange(settings.eraseMember(ws, target.userId), "${target.displayName}'s personal data erased")
    }

    private suspend fun Scope.finishLifecycleChange(
        result: Result<Unit, ApiError>,
        successMessage: String,
    ) {
        when (result) {
            is Result.Ok -> {
                reloadMembers()
                action(MembersSettingsContract.Action.Notify(successMessage, true))
            }
            is Result.Err -> updateState { copy(actionError = result.error.displayMessage) }
        }
    }

    private suspend fun Scope.confirmTransfer(confirmWorkspaceName: String) {
        val state = currentState
        val target = state.transferTo ?: return
        val ws = state.workspaceId.ifBlank { return }
        if (state.transferSubmitting) return
        updateState { copy(transferSubmitting = true, transferError = null, transferNeedsSignIn = false) }
        when (val r = settings.transferOwnership(ws, target.userId, confirmWorkspaceName)) {
            is Result.Ok -> {
                updateState { copy(transferTo = null, transferSubmitting = false) }
                reloadMembers()
                action(MembersSettingsContract.Action.Notify("${target.displayName} is now the owner", true))
                action(MembersSettingsContract.Action.OwnershipTransferred)
            }
            is Result.Err -> {
                val needsSignIn = (r.error as? ApiError.Http)?.code == REAUTH_REQUIRED
                updateState {
                    copy(
                        transferSubmitting = false,
                        transferNeedsSignIn = needsSignIn,
                        transferError = if (needsSignIn) null else r.error.displayMessage,
                    )
                }
            }
        }
    }

    private companion object {
        const val REAUTH_REQUIRED = "reauth-required"
    }

    private suspend fun Scope.submitLinkLinear(linearUserId: String?) {
        val target = currentState.linkLinearFor ?: return
        val ws = currentState.workspaceId.ifBlank { return }
        val payload = UpdateMemberRequestDto(linearUserId = linearUserId.orEmpty())
        when (val r = settings.updateMember(ws, target.userId, payload)) {
            is Result.Ok ->
                updateState { copy(linkLinearFor = null, members = settings.members.toList()) }
            is Result.Err ->
                updateState { copy(linkLinearFor = null, actionError = r.error.displayMessage) }
        }
    }

    private suspend fun Scope.submitLinkGithub(githubLogin: String?) {
        val target = currentState.linkGithubFor ?: return
        val ws = currentState.workspaceId.ifBlank { return }
        val payload = UpdateMemberRequestDto(githubLogin = githubLogin.orEmpty())
        when (val r = settings.updateMember(ws, target.userId, payload)) {
            is Result.Ok ->
                updateState { copy(linkGithubFor = null, members = settings.members.toList()) }
            is Result.Err ->
                updateState { copy(linkGithubFor = null, actionError = r.error.displayMessage) }
        }
    }
}
